import _isEqual from 'lodash-es/isEqual';
import { FieldValue } from '../../..';
import { logger } from '../../../logger';
import { filterNotEmpty } from '../../../forms/utils/listHelpers';
import { TypeaheadOption } from './useApiTypeahead';

type MapInputValueFn<OptionValue, TargetModel> =
  | ((value?: TargetModel) => OptionValue | undefined)
  | ((value?: TargetModel) => OptionValue[])
  | undefined;

export function getSelectedOptions<OptionValue extends any = FieldValue, TargetModel extends any = OptionValue>(
  value: TargetModel | undefined | null,
  options: TypeaheadOption<OptionValue>[],
  multiple: boolean | undefined,
  isInitialValueSet: boolean,
  mapInputValue?: MapInputValueFn<OptionValue, TargetModel>,
  initialValue?: string,
  isEqual?: (optionValue: OptionValue, selectedOptionValue: OptionValue) => boolean,
  onMissingOption?: (value: OptionValue) => TypeaheadOption<OptionValue>
) {
  let selectedOptions: TypeaheadOption<OptionValue>[] = [];
  let currentValues: OptionValue[] | undefined;
  if (value) {
    // if value is a string but meant to be an array and initialValue is set then skip mappings. Initial value is handled later;
    const isArray = multiple;
    const isIgnored = typeof value === 'string' && !!initialValue && isArray;

    try {
      // normalize values (use mapInputValue if exists) and return an array. Single select uses arrays too
      if (!isIgnored) {
        currentValues = filterNotEmpty(
          (mapInputValue
            ? isArray
              ? mapInputValue(value)
              : [mapInputValue(value)]
            : isArray
              ? value
              : [value]) as OptionValue[]
        );
      }
    } catch (e) {
      currentValues = [];
      logger.warn('mapInputValue returns data in the wrong format; ', e);
    }
  }

  // match value(s) against dropdown options
  let hasGenuineMatch = false;
  if (currentValues?.length) {
    selectedOptions = filterNotEmpty(
      (currentValues ?? []).map((currentValue) => {
        const selectedOption = options.find((option) =>
          isEqual ? isEqual(option.value, currentValue) : _isEqual(option.value, currentValue)
        );
        if (selectedOption) {
          hasGenuineMatch = true;
        }

        return selectedOption ?? (onMissingOption ? onMissingOption(currentValue) : undefined);
      })
    );
  }
  // INITIAL VALUE HANDLING (can be a string with/without ";" an option separator).
  // Skip when the value already genuinely matched a real option above (a saved value hydrated
  // in edit mode) so it isn't clobbered by the configured initial value. Gated on a genuine
  // match rather than `selectedOptions.length` because onMissingOption synthesizes a stand-in
  // entry for ANY unmatched value (including the unresolved initialValue placeholder), which
  // would otherwise make selectedOptions non-empty without a real hydrated value being present.
  if (!hasGenuineMatch && !isInitialValueSet && !!initialValue && options.length) {
    const splitInitialValue = initialValue ? `${initialValue}`.split(';').sort() : [];
    selectedOptions = filterNotEmpty(
      splitInitialValue.map((value) =>
        options.find((option) =>
          [
            option.label?.toLowerCase(),
            option.code?.toLowerCase(),
            option.id?.toLowerCase(),
            option.referenceId?.toLowerCase()
          ].includes(value?.toLowerCase())
        )
      )
    );
  }
  return selectedOptions;
}
